import React, { useEffect, useMemo, useState } from "react";
import { Button, Flex, Image, Tooltip } from "@chakra-ui/react";
import { useSessionStore } from "../../../storez/SessionStore";
import { Icon, useTranslation } from "@familyzone/component-library";

const BlockPageLogo: React.FC<{
  bucketName: string;
  logoName?: string;
  logoFile?: File;
  disabled?: boolean;
  onRemove: () => void;
}> = ({ bucketName, logoName, logoFile, disabled, onRemove }) => {
  const { t } = useTranslation();

  const device = useSessionStore.getState().getDevice();

  const [loaded, setLoaded] = useState(false);
  const [removed, setRemoved] = useState(false);

  const handleRemove = () => {
    setRemoved(true);
    onRemove();
  };

  useEffect(() => {
    if (logoName || logoFile) {
      setRemoved(false);
    } else {
      setLoaded(false);
    }
  }, [logoName, logoFile]);

  const imageURL = useMemo(() => {
    if (removed) {
      return "";
    }

    if (logoFile) {
      return URL.createObjectURL(logoFile);
    }

    if (!bucketName) {
      return "";
    }

    // it's possible bucket contains an image while the config has no `logo` set because
    // of past bugs and the image being loaded by device ID, not the presence of config
    // value (both here and on the actual Block Page); therefore, we attempt to display
    // the image regardless of `logo` field being set so that the user would see and be
    // able to remove it, and avoid a scenario where the config page here does not
    // display a logo but the actual Block Page does
    return `https://storage.googleapis.com/${bucketName}/${device.id}/logo?v=${Math.random()}`;
  }, [removed, device.id, bucketName, logoFile]);

  if (!imageURL) {
    return <></>;
  }

  return (
    <Flex hidden={!loaded} direction="column" position="relative">
      <Image
        src={imageURL}
        alt={t("Block Page Logo")}
        hidden={!loaded}
        maxWidth="200px"
        maxHeight="200px"
        borderColor="neutrals.100"
        borderWidth="1px"
        borderStyle="dashed"
        onLoad={() => {
          setLoaded(true);
        }}
        onError={({ currentTarget }) => {
          // SVGs are stored as "logo.svg" whereas all other image types as just "logo" so we need to try both
          if (currentTarget.src.includes("/logo.svg")) {
            currentTarget.onerror = null;
          } else {
            currentTarget.src = currentTarget.src.replace("/logo", "/logo.svg");
          }
        }}
      />
      {loaded && (
        <Flex>
          <Tooltip variant="dark" placement="top" hasArrow={true} maxWidth="336px" label={t("Remove Logo")}>
            <Button
              variant="danger"
              size="sm"
              p="sp8"
              position="absolute"
              top="-12px"
              right="-12px"
              borderRadius="50%"
              aria-label={t("Remove Logo")}
              isDisabled={disabled}
              onClick={handleRemove}
            >
              <Icon icon="fa-close" color="white" boxSize="16px" />
            </Button>
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
};

export default BlockPageLogo;
